import { all, call, fork, put, takeEvery } from 'redux-saga/effects'
import { showLoading, hideLoading } from 'react-redux-loading-bar'
import { notify } from 'reapop'
import FaqService from '../middlewares/faq'
import {
  GET_FAQ_CATEGORIES_REQUEST,
  GET_FAQ_CATEGORIES,
  CREATE_FAQ_CATEGORY_REQUEST,
  CREATE_FAQ_CATEGORY,
  UPDATE_FAQ_CATEGORY_REQUEST,
  UPDATE_FAQ_CATEGORY,
  DELETE_FAQ_CATEGORY_REQUEST,
  DELETE_FAQ_CATEGORY,
  CREATE_FAQ_ITEM_REQUEST,
  CREATE_FAQ_ITEM,
  UPDATE_FAQ_ITEM_REQUEST,
  UPDATE_FAQ_ITEM,
  DELETE_FAQ_ITEM_REQUEST,
  DELETE_FAQ_ITEM,
  GET_FAQ_REQUEST_PENDING,
  GET_FAQ_REQUEST_SUCCESS,
  GET_FAQ_REQUEST_ERROR,
  CHANGE_FAQ_REQUEST_PENDING,
  CHANGE_FAQ_REQUEST_SUCCESS,
  CHANGE_FAQ_REQUEST_ERROR,
} from '../types/faq'

function* getFaqCategoriesWatcher() {
  yield takeEvery(GET_FAQ_CATEGORIES_REQUEST, getFaqCategoriesWorker)
}

function* createFaqCategoryWatcher() {
  yield takeEvery(CREATE_FAQ_CATEGORY_REQUEST, createFaqCategoryWorker)
}

function* updateFaqCategoryWatcher() {
  yield takeEvery(UPDATE_FAQ_CATEGORY_REQUEST, updateFaqCategoryWorker)
}

function* deleteFaqCategoryWatcher() {
  yield takeEvery(DELETE_FAQ_CATEGORY_REQUEST, deleteFaqCategoryWorker)
}

function* createFaqItemWatcher() {
  yield takeEvery(CREATE_FAQ_ITEM_REQUEST, createFaqItemWorker)
}

function* updateFaqItemWatcher() {
  yield takeEvery(UPDATE_FAQ_ITEM_REQUEST, updateFaqItemWorker)
}

function* deleteFaqItemWatcher() {
  yield takeEvery(DELETE_FAQ_ITEM_REQUEST, deleteFaqItemWorker)
}

function* getFaqCategoriesWorker() {
  try {
    yield put(showLoading())
    yield put({ type: GET_FAQ_REQUEST_PENDING })
    const response = yield call(FaqService.getFaqCategories)
    yield put({ type: GET_FAQ_CATEGORIES, payload: response.data.data || [] })
    yield put({ type: GET_FAQ_REQUEST_SUCCESS })
  } catch (error) {
    yield put({
      type: GET_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to fetch FAQ categories' }],
    })
  } finally {
    yield put(hideLoading())
  }
}

function* createFaqCategoryWorker({ payload }) {
  const { data, onSuccess, onError } = payload || {}
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_FAQ_REQUEST_PENDING })
    const response = yield call(FaqService.createFaqCategory, data || payload)
    yield put({ type: CREATE_FAQ_CATEGORY, payload: response.data.data })
    yield put({ type: CHANGE_FAQ_REQUEST_SUCCESS })
    yield call(getFaqCategoriesWorker)
    yield put(notify({ status: 'success', title: 'FAQ category created', dismissAfter: 3000 }))
    if (typeof onSuccess === 'function') {
      yield call(onSuccess, response.data.data)
    }
  } catch (error) {
    yield put({
      type: CHANGE_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to create FAQ category' }],
    })
    if (typeof onError === 'function') {
      yield call(onError, error)
    }
  } finally {
    yield put(hideLoading())
  }
}

function* updateFaqCategoryWorker({ payload }) {
  const { id, data, onSuccess, onError } = payload || {}
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_FAQ_REQUEST_PENDING })
    const response = yield call(FaqService.updateFaqCategory, id, data)
    yield put({ type: UPDATE_FAQ_CATEGORY, payload: response.data.data })
    yield put({ type: CHANGE_FAQ_REQUEST_SUCCESS })
    yield call(getFaqCategoriesWorker)
    yield put(notify({ status: 'success', title: 'FAQ category updated', dismissAfter: 3000 }))
    if (typeof onSuccess === 'function') {
      yield call(onSuccess, response.data.data)
    }
  } catch (error) {
    yield put({
      type: CHANGE_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to update FAQ category' }],
    })
    if (typeof onError === 'function') {
      yield call(onError, error)
    }
  } finally {
    yield put(hideLoading())
  }
}

function* deleteFaqCategoryWorker({ payload }) {
  const { id, onSuccess, onError } = payload || {}
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_FAQ_REQUEST_PENDING })
    yield call(FaqService.deleteFaqCategory, id)
    yield put({ type: DELETE_FAQ_CATEGORY, payload: id })
    yield put({ type: CHANGE_FAQ_REQUEST_SUCCESS })
    yield call(getFaqCategoriesWorker)
    yield put(notify({ status: 'success', title: 'FAQ category deleted', dismissAfter: 3000 }))
    if (typeof onSuccess === 'function') {
      yield call(onSuccess)
    }
  } catch (error) {
    yield put({
      type: CHANGE_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to delete FAQ category' }],
    })
    if (typeof onError === 'function') {
      yield call(onError, error)
    }
  } finally {
    yield put(hideLoading())
  }
}

function* createFaqItemWorker({ payload }) {
  const { categoryId, data, onSuccess, onError } = payload || {}
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_FAQ_REQUEST_PENDING })
    const response = yield call(FaqService.createFaqItem, categoryId, data)
    yield put({ type: CREATE_FAQ_ITEM, payload: response.data.data })
    yield put({ type: CHANGE_FAQ_REQUEST_SUCCESS })
    yield call(getFaqCategoriesWorker)
    yield put(notify({ status: 'success', title: 'FAQ item created', dismissAfter: 3000 }))
    if (typeof onSuccess === 'function') {
      yield call(onSuccess, response.data.data)
    }
  } catch (error) {
    yield put({
      type: CHANGE_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to create FAQ item' }],
    })
    if (typeof onError === 'function') {
      yield call(onError, error)
    }
  } finally {
    yield put(hideLoading())
  }
}

function* updateFaqItemWorker({ payload }) {
  const { categoryId, itemId, data, onSuccess, onError } = payload || {}
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_FAQ_REQUEST_PENDING })
    const response = yield call(FaqService.updateFaqItem, categoryId, itemId, data)
    yield put({ type: UPDATE_FAQ_ITEM, payload: response.data.data })
    yield put({ type: CHANGE_FAQ_REQUEST_SUCCESS })
    yield call(getFaqCategoriesWorker)
    yield put(notify({ status: 'success', title: 'FAQ item updated', dismissAfter: 3000 }))
    if (typeof onSuccess === 'function') {
      yield call(onSuccess, response.data.data)
    }
  } catch (error) {
    yield put({
      type: CHANGE_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to update FAQ item' }],
    })
    if (typeof onError === 'function') {
      yield call(onError, error)
    }
  } finally {
    yield put(hideLoading())
  }
}

function* deleteFaqItemWorker({ payload }) {
  const { categoryId, itemId, onSuccess, onError } = payload || {}
  try {
    yield put(showLoading())
    yield put({ type: CHANGE_FAQ_REQUEST_PENDING })
    yield call(FaqService.deleteFaqItem, categoryId, itemId)
    yield put({ type: DELETE_FAQ_ITEM, payload: itemId })
    yield put({ type: CHANGE_FAQ_REQUEST_SUCCESS })
    yield call(getFaqCategoriesWorker)
    yield put(notify({ status: 'success', title: 'FAQ item deleted', dismissAfter: 3000 }))
    if (typeof onSuccess === 'function') {
      yield call(onSuccess)
    }
  } catch (error) {
    yield put({
      type: CHANGE_FAQ_REQUEST_ERROR,
      payload: error?.response?.data?.messages || [{ message: 'Failed to delete FAQ item' }],
    })
    if (typeof onError === 'function') {
      yield call(onError, error)
    }
  } finally {
    yield put(hideLoading())
  }
}

export default function* faq() {
  yield all([
    fork(getFaqCategoriesWatcher),
    fork(createFaqCategoryWatcher),
    fork(updateFaqCategoryWatcher),
    fork(deleteFaqCategoryWatcher),
    fork(createFaqItemWatcher),
    fork(updateFaqItemWatcher),
    fork(deleteFaqItemWatcher),
  ])
}
